// @cursor-model gpt-4o-mini
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import HomePage from './pages/HomePage';
import DeckDetailsPage from './pages/DeckDetailsPage';
import UserPage from './pages/UserPage';
import DeckLibraryPage from './pages/DeckLibraryPage';
import CreateDeckPage from './pages/CreateDeckPage';
import ClassesPage from './pages/ClassesPage';
import StudyPage from './pages/StudyPage';
import SignInPage from './pages/SignInPage';
import CreateClassPage from './pages/CreateClassPage';
import Header from './components/Header';
import Box from '@mui/material/Box';
import ClassDetailsPage from './pages/ClassDetailsPage';
import JoinClassPage from './pages/JoinClassPage';
import PublicDecksPage from './pages/PublicDecksPage';
import EditDeckPage from './pages/EditDeckPage';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2B2D42', // Traditional Japanese black
      light: '#4A4E69',
      dark: '#1A1B2E',
    },
    secondary: {
      main: '#EF233C', // Japanese red
      light: '#FF4D6D',
      dark: '#D90429',
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2B2D42',
      secondary: '#4A4E69',
    },
  },
  typography: {
    fontFamily: '"Noto Sans JP", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Noto Serif JP", serif',
      fontWeight: 500,
    },
    h2: {
      fontFamily: '"Noto Serif JP", serif',
      fontWeight: 500,
    },
    h3: {
      fontFamily: '"Noto Serif JP", serif',
      fontWeight: 500,
    },
    h4: {
      fontFamily: '"Noto Serif JP", serif',
      fontWeight: 500,
    },
    h5: {
      fontFamily: '"Noto Serif JP", serif',
      fontWeight: 500,
    },
    h6: {
      fontFamily: '"Noto Serif JP", serif',
      fontWeight: 500,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          background: 'rgba(255, 255, 255, 0.9)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background: `
            linear-gradient(135deg, rgba(43, 45, 66, 0.05), rgba(239, 35, 60, 0.05)),
            repeating-linear-gradient(45deg, rgba(43, 45, 66, 0.02) 0px, rgba(43, 45, 66, 0.02) 2px, transparent 2px, transparent 4px),
            repeating-linear-gradient(-45deg, rgba(239, 35, 60, 0.02) 0px, rgba(239, 35, 60, 0.02) 2px, transparent 2px, transparent 4px),
            linear-gradient(90deg, #ffffff, #f8f9fa)
          `,
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `
              radial-gradient(circle at 100% 0%, rgba(239, 35, 60, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 0% 100%, rgba(43, 45, 66, 0.1) 0%, transparent 50%)
            `,
            zIndex: -1,
          }
        }}
      >
        <AuthProvider>
          <LanguageProvider>
            <Router>
              <Header />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/signin" element={<SignInPage />} />
                <Route path="/user" element={<UserPage />} />
                <Route path="/decks" element={<DeckLibraryPage />} />
                <Route path="/decks/:deckId" element={<DeckDetailsPage />} />
                <Route path="/edit-deck/:deckId" element={<EditDeckPage />} />
                <Route path="/create-deck" element={<CreateDeckPage />} />
                <Route path="/classes" element={<ClassesPage />} />
                <Route path="/classes/new" element={<CreateClassPage />} />
                <Route path="/study/:deckId" element={<StudyPage />} />
                <Route path="/classes/:classId" element={<ClassDetailsPage />} />
                <Route path="/join-class/:classId" element={<JoinClassPage />} />
                <Route path="/public-decks" element={<PublicDecksPage />} />
              </Routes>
            </Router>
          </LanguageProvider>
        </AuthProvider>
      </Box>
    </ThemeProvider>
  );
}

export default App; 